import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, DetailCardLayout } from './common/DetailCardAndPinLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemTitle from './common/ItemTitle'
import Attachments from './common/Attachments'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import type { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import useToggle from 'hooks/useToggle'
import useOffer from 'hooks/Offers/useOffer'
import type {
  CruiseItem,
  SavedCruiseData,
  TourItem,
} from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  formatSingleDateAndTimeRange,
  getOfferImage,
  isCustomItem,
  isLeBookmark,
} from 'tripPlanner/utils'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import EditTripItem from '../TripModal/TripItems/Edit'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useNavigateTo from 'hooks/useNavigateTo'
import { isTourV1Offer, isTourV2Offer } from 'lib/offer/offerTypes'
import TourV2PriceAndCTAs from './PriceAndCTAs/TourV2PriceAndCTAs'
import TourV1PriceAndCTAs from './PriceAndCTAs/TourV1PriceAndCTAs'
import ItemPriceAndCTAs from './common/ItemPriceAndCTAs'
import BookedOfferDetailsCTA from './CTAContent/BookedOfferDetailsCTA'
import CruisePriceAndCTAs from './PriceAndCTAs/CruisePriceAndCTAs'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'
import clsx from 'clsx'

function isV2CruiseItem(item: CruiseItem | TourItem): item is CruiseItem & { savedItemData: Extract<SavedCruiseData, { offerType: 'cruise' }> } {
  return item.type === 'CRUISE' && item.savedItemData?.offerType === 'cruise'
}

interface Props {
  item: TourItem | CruiseItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailTourCruiseCard({ item, variant }: Props) {
  const intl = useIntl()

  const locationLabel = item.endPlace && item.endPlace.name !== item.startPlace.name
    ? intl.formatMessage(
        {
          id: 'WH3nQgvWYtcwZv0yujop',
          defaultMessage: 'Starts in {startLocation}, Ends in {endLocation}',
          description: 'Detail tour/cruise card showing distinct start and end locations',
        },
        { startLocation: item.startPlace.name, endLocation: item.endPlace.name },
      )
    : item.startPlace.name

  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)
  const offerId = 'code' in item ? item.code : undefined
  // The query type includes many offer types, but this card is only used for tour offers
  const [_offer] = useOffer(offerId)
  const offer = _offer && (_offer.parentType === 'tour' || _offer.parentType === 'tour_v2' || _offer.parentType === 'cruise') ? _offer : undefined

  const { data: trip } = useTrip({ tripId: item.tripId })
  const timeFormat = useRegionTimeFormat()
  const offerLink = isLeBookmark(item) ? buildProductLink(item, { offer }) : undefined

  const { id: imgId, url: imgSrc } = getOfferImage(offer)

  const timeDescription =
    item.startDate && item.endDate && !item.endDate.isSame(item.startDate) ?
        formatShortDateRange({
          startDate: item.startDate,
          endDate: item.endDate,
        }) :
        formatSingleDateAndTimeRange(item, timeFormat)

  const startPlaceId = 'id' in item.startPlace ? item.startPlace.id : undefined

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const onClickAddTripItemDates = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const dateWarningIcon =
    variant !== 'drawer' ?
        <OutsideTripDatesIcon
          item={item}
          changeItemDates={onClickAddTripItemDates}
        /> :
      undefined

  let bottomContent: React.ReactElement | null = null

  if (variant === 'itinerary') {
    // No bottom content for itinerary
  } else if (item.isBooked && item.confirmationCode) {
    bottomContent = <ItemPriceAndCTAs>
      <div className="cta-area"><BookedOfferDetailsCTA item={item} size="medium" /></div>
    </ItemPriceAndCTAs>
  } else if (isLeBookmark(item) && isTourV1Offer(offer)) {
    bottomContent = (
      <TourV1PriceAndCTAs
        item={item}
        offer={offer}
        variant={variant}
      />
    )
  } else if (isLeBookmark(item) && item.type === 'TOUR' && isTourV2Offer(offer)) {
    bottomContent = (
      <TourV2PriceAndCTAs
        item={item}
        offer={offer}
        variant={variant}
      />
    )
  } else if (isLeBookmark(item) && isV2CruiseItem(item)) {
    bottomContent = (
      <CruisePriceAndCTAs
        item={item}
        variant={variant}
      />
    )
  }

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-tour">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <DetailCardAndPinLayout.CardContainer>
        <DetailCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          <ItemCardImage
            item={item}
            name={item.startPlace.name}
            imgId={imgId ?? item.savedItemData?.imageIds?.[0]}
            imgSrc={imgSrc}
            placeId={startPlaceId}
            className={clsx({ 'showComment': showComment })}
          />
        </DetailCardLayout.Image>
        <DetailCardLayout.Content>
          <CornerControls item={item} offerLink={offerLink} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <BodyText variant="small" colour="neutral-two">
                {locationLabel}
              </BodyText>
              <ItemTitle>{item.name}</ItemTitle>
            </VerticalSpacer>
            {timeDescription && (
              <BodyTextBlock
                variant="small"
                colour="neutral-two"
                endIcon={dateWarningIcon}
              >
                {timeDescription}
              </BodyTextBlock>
            )}
            <Attachments item={item} />
            <PrintOnlyAddressAndNotes item={item} />
            {bottomContent}
          </VerticalSpacer>
        </DetailCardLayout.Content>
        {showComment && (
          <DetailCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </DetailCardLayout.Comment>
        )}
      </DetailCardAndPinLayout.CardContainer>
    </DetailCardAndPinLayout>
  )
}

export default DetailTourCruiseCard
